import { useEffect, useState } from 'react';
import { Autocomplete, Chip, Skeleton, TextField } from '@mui/material';
import customAxios from '../../utils/customAxios';

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

const getNestedValue = (obj, path, defaultValue = undefined) => {
  return (
    path
      .split('.')
      .reduce(
        (acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined),
        obj,
      ) || defaultValue
  );
};

export const AutocompleteFilterInput = ({
  item,
  applyValue,
  endpoint,
  label,
  isMultiple = false,
  labelKey,
  idKey,
  dataPath = 'data',
  useIdAsValue = false,
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [options, setOptions] = useState([]);

  const fetchOptions = async () => {
    setIsFetching(true);
    try {
      const response = await customAxios.get(endpoint, HEADERS);

      if (response.status === 204) {
        console.warn(
          `[AutocompleteFilterInput] The response from ${endpoint} returned status 204 (No Content).`,
        );
        setOptions([]);
        return;
      }

      const fetchedData = getNestedValue(response, dataPath, undefined);

      if (!Array.isArray(fetchedData)) {
        console.warn(
          `[AutocompleteFilterInput] The dataPath "${dataPath}" does not resolve to an array. ` +
            `Ensure the response from ${endpoint} has the correct structure. ` +
            `Falling back to an empty options list.`,
        );
        setOptions([]);
        return;
      }

      setOptions(fetchedData);
    } catch (error) {
      console.error(
        `[AutocompleteFilterInput] Failed to fetch options from ${endpoint}:`,
        error,
      );
      setOptions([]);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The key used to match/deduplicate options internally (idKey if provided, else labelKey)
  const matchKey = idKey || labelKey;
  // The key whose value is sent to the server filter
  const valueKey = useIdAsValue ? matchKey : labelKey;

  const handleFilterChange = (newValue) => {
    if (isMultiple) {
      const selectedValues = newValue.map((val) => val[valueKey]);
      applyValue({ ...item, value: selectedValues });
    } else {
      const selectedValue = newValue ? newValue[valueKey] : null;
      applyValue({ ...item, value: selectedValue });
    }
  };

  // Derive selected value(s) directly from item.value + loaded options — no intermediate state
  // Match against valueKey since that is what was stored by handleFilterChange
  const selectedValue = isMultiple
    ? options.filter(
        (option) =>
          Array.isArray(item.value) && item.value.includes(option[valueKey]),
      )
    : options.find((option) => option[valueKey] === item.value) || null;

  if (isFetching) {
    return (
      <div style={{ marginTop: 'auto' }}>
        <Skeleton height={36} variant="rectangular" />
      </div>
    );
  }

  return (
    <Autocomplete
      multiple={isMultiple}
      limitTags={isMultiple ? 1 : undefined}
      options={options}
      value={selectedValue}
      filterSelectedOptions={isMultiple}
      getOptionLabel={(option) => {
        // Add id label to 'Sucursal' options because of possible duplicate names
        if (label === 'Sucursal') {
          return option[idKey]
            ? option[labelKey] + ' - ' + option[idKey]
            : option[labelKey];
        } else {
          return option[labelKey];
        }
      }}
      isOptionEqualToValue={(option, value) =>
        option[matchKey] === value[matchKey]
      }
      renderInput={(params) => (
        <TextField
          variant="standard"
          label={label}
          placeholder={`${label}...`}
          InputLabelProps={{ shrink: true }}
          {...params}
        />
      )}
      onChange={(_, newValue) => handleFilterChange(newValue)}
      renderTags={(tagValue, getTagProps) =>
        isMultiple
          ? tagValue.map((option, index) => (
              <Chip
                size="small"
                key={option.id}
                label={option[labelKey]}
                {...getTagProps({ index })}
              />
            ))
          : null
      }
    />
  );
};
